/**
 * Page and API addresses as error tracking may keep them (GDPR review 19.10.2026, sister of #1356).
 *
 * A code in some paths opens a page or its data without signing in (a join link, a participant's
 * journey, a client view, a shared reading), and the sign-in link and invite calls carry the email
 * address and one-time codes in the query. Error tracking gets the path with such codes replaced
 * and no query or hash, in events, breadcrumbs and the spans of sampled transactions.
 */
const CODE_PATHS: Array<[RegExp, string]> = [
    // API calls whose path carries such a code (the backend twin, backend/src/utils/telemetryUrl.ts, lists the same).
    [/^\/api\/(sessions\/public|studies\/public|studies\/share|programmes\/journey|programmes\/customer|programmes\/present|spinagate\/link|spinagate\/r|languages)\/[^/]+/, '/api/$1/:code'],
    [/^\/join\/study\/[^/]+/, '/join/study/:code'],
    [/^\/join\/(?!study(\/|$))[^/]+/, '/join/:code'],
    [/^\/spinagate\/r\/[^/]+/, '/spinagate/r/:code'],
    [/^\/(client|present|embed|session|journey|summary)\/[^/]+/, '/$1/:code'],
];

export function telemetrySafePath(pathname: string): string {
    return CODE_PATHS.reduce((path, [pattern, replacement]) => path.replace(pattern, replacement), pathname);
}

/** A full or relative address with codes masked and the query and hash dropped. */
export function telemetrySafeUrl(href: string): string {
    try {
        const absolute = /^[a-z][a-z0-9+.-]*:/i.test(href);
        const url = new URL(href, 'https://artofthinking.ai');
        const path = telemetrySafePath(url.pathname);
        return absolute ? `${url.origin}${path}` : path;
    } catch {
        return href.split(/[?#]/)[0];
    }
}

/** Free text that is an address, or a method and an address ("GET /api/…", a span name). */
export function telemetrySafeText(text: string): string {
    const match = /^([A-Z]+ )?(\S+)$/.exec(text);
    if (!match) return text;
    const [, method = '', address] = match;
    if (!address.startsWith('/') && !/^https?:\/\//i.test(address)) return text;
    return `${method}${telemetrySafeUrl(address)}`;
}

type Span = { description?: string; data?: Record<string, unknown> };

type ScrubbableEvent = {
    request?: { url?: string; query_string?: unknown; headers?: Record<string, string> };
    transaction?: string;
    tags?: Record<string, unknown>;
    breadcrumbs?: Array<{ data?: Record<string, unknown> }>;
    spans?: Span[];
    contexts?: { trace?: { data?: Record<string, unknown> } };
};

/** Scrubs one Sentry event or transaction in place and returns it. */
export function scrubTelemetryEvent<T extends ScrubbableEvent>(event: T): T {
    if (event.request) {
        if (event.request.url) event.request.url = telemetrySafeUrl(event.request.url);
        delete event.request.query_string;
        if (event.request.headers?.Referer) event.request.headers.Referer = telemetrySafeUrl(event.request.headers.Referer);
        if (event.request.headers?.referer) event.request.headers.referer = telemetrySafeUrl(event.request.headers.referer);
    }
    if (typeof event.transaction === 'string' && event.transaction.startsWith('/')) {
        event.transaction = telemetrySafeUrl(event.transaction);
    }
    if (typeof event.tags?.url === 'string') event.tags.url = telemetrySafeUrl(event.tags.url);
    for (const crumb of event.breadcrumbs ?? []) scrubBreadcrumbData(crumb.data);
    // A sampled transaction's API call spans are named "GET /api/…" and carry the full address and query.
    for (const span of event.spans ?? []) {
        if (typeof span.description === 'string') span.description = telemetrySafeText(span.description);
        scrubBreadcrumbData(span.data);
    }
    scrubBreadcrumbData(event.contexts?.trace?.data);
    return event;
}

const URL_KEYS = ['from', 'to', 'url', 'http.url', 'url.full', 'http.target', 'url.path'];
const QUERY_KEYS = ['http.query', 'http.fragment', 'url.query', 'url.fragment'];

/** Navigation and fetch breadcrumbs, and spans, carry addresses under a few keys and the query on its own. */
export function scrubBreadcrumbData(data: Record<string, unknown> | undefined): void {
    if (!data) return;
    for (const key of URL_KEYS) {
        if (typeof data[key] === 'string') data[key] = telemetrySafeUrl(data[key] as string);
    }
    for (const key of QUERY_KEYS) delete data[key];
}
